import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Bookmark, Loader2, Plus, Trash2, Edit2, Upload, FileText } from 'lucide-react';
import { apiRequest } from '@/utils/api-request';
import { showToast } from '@/components/ui/Toast';
import RichTextEditor from '@/components/ui/RichTextEditor';
import Cookies from 'js-cookie';

interface FeeRange {
    id: number;
    curriculum_id_fk: number;
    curriculum_name: string;
    grade_from: string;
    grade_to: string;
    min_fee: string;
    max_fee: string;
    fee_range: string;
}

interface Board {
    board_id: number;
    board_name: string;
}

interface AdmissionsData {
    admission_info: {
        admission_procedure: string;
        admission_documents: string;
        brochure: string;
    };
    admissions_open: boolean;
    fee_ranges: FeeRange[];
    boards: Board[];
}

const GRADE_OPTIONS = ["Pre-school", "LKG", "UKG", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];

const Admissions: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [data, setData] = useState<AdmissionsData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [editingFeeRange, setEditingFeeRange] = useState<FeeRange | null>(null);
    const [showFeeRangeForm, setShowFeeRangeForm] = useState(false);
    const [uploadingBrochure, setUploadingBrochure] = useState(false);

    // Fee Range Form State
    const [feeRangeForm, setFeeRangeForm] = useState({
        curriculum_id_fk: '',
        grade_from: '',
        grade_to: '',
        min_fee: '',
        max_fee: ''
    });

    useEffect(() => {
        fetchAdmissionsData();
    }, [id]);

    const fetchAdmissionsData = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiRequest(`/admissions/${id}`);
            if (response.success && response.data) {
                setData(response.data);
            } else {
                setError(response.message || 'Failed to load admissions information');
            }
        } catch (err: any) {
            console.error('Error fetching admissions data:', err);
            setError('Failed to load admissions information');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!data) return;

        try {
            setSaving(true);
            setError(null);

            const payload = {
                admission_procedure: data.admission_info.admission_procedure,
                admission_documents: data.admission_info.admission_documents,
                admissions_open: data.admissions_open
            };

            const response = await apiRequest(`/admissions/${id}`, {
                method: 'PUT',
                body: payload,
            });

            if (response.success) {
                showToast('Admissions information updated successfully!', 'success');
            } else {
                throw new Error(response.message || 'Failed to update');
            }
        } catch (err: any) {
            console.error('Error saving admissions data:', err);
            const errorMessage = err.message || 'Failed to save admissions information';
            setError(errorMessage);
            showToast(errorMessage, 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleBrochureUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/jpg', 'image/png'];
        if (!allowedTypes.includes(file.type)) {
            showToast('Only PDF, DOC, DOCX, JPG, JPEG, and PNG files are allowed', 'error');
            return;
        }

        // Validate file size (10MB)
        if (file.size > 10 * 1024 * 1024) {
            showToast('File size must be less than 10MB', 'error');
            return;
        }

        try {
            setUploadingBrochure(true);
            const formData = new FormData();
            formData.append('brochure', file);

            // Get auth token from cookie
            const token = Cookies.get('crm_auth_token');
            const headers: Record<string, string> = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
            const response = await fetch(`${API_BASE_URL}/admissions/${id}/upload-brochure`, {
                method: 'POST',
                headers,
                credentials: 'include',
                body: formData
            });

            const result = await response.json();

            if (result.success) {
                showToast('Brochure uploaded successfully!', 'success');
                // Update local state
                setData(prev => prev ? {
                    ...prev,
                    admission_info: {
                        ...prev.admission_info,
                        brochure: result.data.filename
                    }
                } : null);
            } else {
                throw new Error(result.message || 'Failed to upload');
            }
        } catch (err: any) {
            console.error('Error uploading brochure:', err);
            showToast(err.message || 'Failed to upload brochure', 'error');
        } finally {
            setUploadingBrochure(false);
        }
    };

    const handleAddFeeRange = () => {
        setFeeRangeForm({
            curriculum_id_fk: '',
            grade_from: '',
            grade_to: '',
            min_fee: '',
            max_fee: ''
        });
        setEditingFeeRange(null);
        setShowFeeRangeForm(true);
    };

    const handleEditFeeRange = (feeRange: FeeRange) => {
        setFeeRangeForm({
            curriculum_id_fk: feeRange.curriculum_id_fk.toString(),
            grade_from: feeRange.grade_from,
            grade_to: feeRange.grade_to,
            min_fee: feeRange.min_fee,
            max_fee: feeRange.max_fee
        });
        setEditingFeeRange(feeRange);
        setShowFeeRangeForm(true);
    };

    const handleSaveFeeRange = async () => {
        if (!feeRangeForm.curriculum_id_fk || !feeRangeForm.grade_from || !feeRangeForm.grade_to) {
            showToast('Please fill in all required fields', 'error');
            return;
        }

        try {
            const payload = {
                curriculum_id_fk: parseInt(feeRangeForm.curriculum_id_fk),
                grade_from: feeRangeForm.grade_from,
                grade_to: feeRangeForm.grade_to,
                min_fee: feeRangeForm.min_fee,
                max_fee: feeRangeForm.max_fee,
                fee_range: '' // Can be calculated or left empty
            };

            let response;
            if (editingFeeRange) {
                // Update existing
                response = await apiRequest(`/admissions/fee-ranges/${editingFeeRange.id}`, {
                    method: 'PUT',
                    body: payload
                });
            } else {
                // Add new
                response = await apiRequest(`/admissions/${id}/fee-ranges`, {
                    method: 'POST',
                    body: payload
                });
            }

            if (response.success) {
                showToast(editingFeeRange ? 'Fee range updated successfully!' : 'Fee range added successfully!', 'success');
                setShowFeeRangeForm(false);
                fetchAdmissionsData(); // Reload data
            } else {
                throw new Error(response.message || 'Failed to save fee range');
            }
        } catch (err: any) {
            console.error('Error saving fee range:', err);
            showToast(err.message || 'Failed to save fee range', 'error');
        }
    };

    const handleDeleteFeeRange = async (rangeId: number) => {
        if (!confirm('Are you sure you want to delete this fee range?')) {
            return;
        }

        try {
            const response = await apiRequest(`/admissions/fee-ranges/${rangeId}`, {
                method: 'DELETE'
            });

            if (response.success) {
                showToast('Fee range deleted successfully!', 'success');
                fetchAdmissionsData(); // Reload data
            } else {
                throw new Error(response.message || 'Failed to delete fee range');
            }
        } catch (err: any) {
            console.error('Error deleting fee range:', err);
            showToast(err.message || 'Failed to delete fee range', 'error');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <Loader2 className="h-8 w-8 text-brandColor animate-spin" />
            </div>
        );
    }

    if (error && !data) {
        return (
            <div className="p-8">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <p className="text-red-800 dark:text-red-200">{error}</p>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="p-8">
                <p className="text-gray-500">No data available</p>
            </div>
        );
    }

    return (
        <div>
            {/* Header - Fixed */}
            <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-8 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                            <div className="p-2 bg-brandColor rounded-lg text-white">
                                <Bookmark className="w-6 h-6" />
                            </div>
                        </div>
                        <div className="flex-1 min-w-0">
                            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Admissions</h1>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Manage admission procedures, documents, and fee structure</p>
                        </div>
                    </div>
                    <div className="flex-shrink-0">
                        <button
                            type="submit"
                            form="admissions-form"
                            disabled={saving}
                            className="inline-flex items-center px-4 py-2 bg-brandColor hover:bg-brand-hover text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandColor text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
                        </button>
                    </div>
                </div>
            </div>

            {error && (
                <div className="mx-8 mt-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <p className="text-red-800 dark:text-red-200">{error}</p>
                </div>
            )}

            {/* Form */}
            <form id="admissions-form" onSubmit={handleSubmit} className="p-8 space-y-6">
                {/* Admissions Open Toggle */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-6 py-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-base font-semibold text-gray-900 dark:text-white">Admissions Status</h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                    Toggle to open or close admissions for this school
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setData({ ...data, admissions_open: !data.admissions_open })}
                                className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brandColor focus:ring-offset-2 ${
                                    data.admissions_open ? 'bg-brandColor' : 'bg-gray-200 dark:bg-gray-700'
                                }`}
                            >
                                <span
                                    className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                                        data.admissions_open ? 'translate-x-7' : 'translate-x-1'
                                    }`}
                                />
                            </button>
                        </div>
                        <div className="mt-3">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                                data.admissions_open
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                    : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                            }`}>
                                {data.admissions_open ? 'Admissions Open' : 'Admissions Closed'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Admission Procedure */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="bg-brandColor px-6 py-3">
                        <h2 className="text-lg font-semibold text-white">Admission Procedure</h2>
                    </div>
                    <div className="p-6">
                        <RichTextEditor
                            value={data.admission_info.admission_procedure || ''}
                            onChange={(value) => setData({
                                ...data,
                                admission_info: { ...data.admission_info, admission_procedure: value }
                            })}
                            placeholder="Describe the step-by-step admission process..."
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            Provide detailed information about the admission process
                        </p>
                    </div>
                </div>

                {/* Required Documents */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="bg-brandColor px-6 py-3">
                        <h2 className="text-lg font-semibold text-white">Required Documents</h2>
                    </div>
                    <div className="p-6">
                        <RichTextEditor
                            value={data.admission_info.admission_documents || ''}
                            onChange={(value) => setData({
                                ...data,
                                admission_info: { ...data.admission_info, admission_documents: value }
                            })}
                            placeholder="List all required documents for admission..."
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            List all documents parents need to submit during admission
                        </p>
                    </div>
                </div>

                {/* Admission Brochure */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="bg-brandColor px-6 py-3">
                        <h2 className="text-lg font-semibold text-white">Admission Brochure</h2>
                    </div>
                    <div className="p-6">
                        <div className="flex items-center gap-4">
                            {data.admission_info.brochure && (
                                <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                                    <FileText className="w-5 h-5" />
                                    <span>{data.admission_info.brochure}</span>
                                </div>
                            )}
                            <label className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                                <input
                                    type="file"
                                    className="hidden"
                                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                    onChange={handleBrochureUpload}
                                    disabled={uploadingBrochure}
                                />
                                {uploadingBrochure ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        <span className="text-sm">Uploading...</span>
                                    </>
                                ) : (
                                    <>
                                        <Upload className="w-4 h-4 mr-2" />
                                        <span className="text-sm">{data.admission_info.brochure ? 'Replace Brochure' : 'Upload Brochure'}</span>
                                    </>
                                )}
                            </label>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            Upload PDF, DOC, DOCX, JPG, JPEG, or PNG files (Max 10MB)
                        </p>
                    </div>
                </div>

                {/* Fee Structure */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="bg-brandColor px-6 py-3 flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-white">Board-wise Fee Structure</h2>
                        <button
                            type="button"
                            onClick={handleAddFeeRange}
                            className="inline-flex items-center px-3 py-1 bg-white text-brandColor rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium"
                        >
                            <Plus className="w-4 h-4 mr-1" />
                            Add Fee Range
                        </button>
                    </div>
                    <div className="p-6">
                        {data.fee_ranges.length === 0 ? (
                            <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                                No fee ranges added yet. Click "Add Fee Range" to get started.
                            </p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 dark:bg-gray-700">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                                                Board/Curriculum
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                                                Grade Range
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                                                Min Fee
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                                                Max Fee
                                            </th>
                                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                        {data.fee_ranges.map((range) => (
                                            <tr key={range.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                                    {range.curriculum_name}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                                    {range.grade_from} - {range.grade_to}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                                    ₹{range.min_fee}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                                    ₹{range.max_fee}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-right">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleEditFeeRange(range)}
                                                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 mr-3"
                                                    >
                                                        <Edit2 className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDeleteFeeRange(range.id)}
                                                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </form>

            {/* Fee Range Form Modal */}
            {showFeeRangeForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
                        <div className="bg-brandColor px-6 py-4">
                            <h3 className="text-lg font-semibold text-white">
                                {editingFeeRange ? 'Edit Fee Range' : 'Add Fee Range'}
                            </h3>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Board/Curriculum *
                                </label>
                                <select
                                    value={feeRangeForm.curriculum_id_fk}
                                    onChange={(e) => setFeeRangeForm({ ...feeRangeForm, curriculum_id_fk: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                    required
                                >
                                    <option value="">Select Board</option>
                                    {data.boards.map((board) => (
                                        <option key={board.board_id} value={board.board_id}>
                                            {board.board_name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Grade From *
                                    </label>
                                    <select
                                        value={feeRangeForm.grade_from}
                                        onChange={(e) => setFeeRangeForm({ ...feeRangeForm, grade_from: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                        required
                                    >
                                        <option value="">Select</option>
                                        {GRADE_OPTIONS.map((grade) => (
                                            <option key={grade} value={grade}>
                                                {grade}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Grade To *
                                    </label>
                                    <select
                                        value={feeRangeForm.grade_to}
                                        onChange={(e) => setFeeRangeForm({ ...feeRangeForm, grade_to: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                        required
                                    >
                                        <option value="">Select</option>
                                        {GRADE_OPTIONS.map((grade) => (
                                            <option key={grade} value={grade}>
                                                {grade}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Min Fee (₹)
                                    </label>
                                    <input
                                        type="text"
                                        value={feeRangeForm.min_fee}
                                        onChange={(e) => setFeeRangeForm({ ...feeRangeForm, min_fee: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                        placeholder="50000"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Max Fee (₹)
                                    </label>
                                    <input
                                        type="text"
                                        value={feeRangeForm.max_fee}
                                        onChange={(e) => setFeeRangeForm({ ...feeRangeForm, max_fee: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor"
                                        placeholder="75000"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 flex justify-end gap-3 rounded-b-lg">
                            <button
                                type="button"
                                onClick={() => setShowFeeRangeForm(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSaveFeeRange}
                                className="px-4 py-2 text-sm font-medium text-white bg-brandColor hover:bg-brand-hover rounded-lg transition-colors"
                            >
                                {editingFeeRange ? 'Update' : 'Add'} Fee Range
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Admissions;
